import React, { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { useSelector, useDispatch, connect } from 'react-redux'

import { setDraggedFile, showCreateSiteModal } from 'Manage/actions'
import { isPDF, isValidFileSize, pluralize, onUpgradePlan } from 'utils/general'
import { PRO_MAX_FILE_SIZES } from 'constants/plans'
import Placeholder from './Placeholder'
import { useLocation } from 'react-router-dom'
import { showUpgradeCardModal } from 'actions'
import { MAX_FILE_SIZE, MAX_PDF_SIZE, DROPZONE_ACCEPTED_FILES, MAX_FILE_SIZE_FREE } from '../../../constants'
import { ACTION_CREATE, isZipFile } from '../../CreateUpdateSiteModal/interface'
import { PLANS } from 'constants/plans'
import { isLottieFile } from 'helpers/file-type'
import { isFolderDrop, createMockFolderFile } from 'utils/folder-upload'

const DropzoneWrapper = ({
  children,
  className,
  siteLimit,
  trialAccount,
  liveSitesCount = 0,
  updateModalOpen,
  acceptedFiles = DROPZONE_ACCEPTED_FILES
}) => {
  const location = useLocation()
  const dispatch = useDispatch()

  const [isDragging, setIsDragging] = useState(false)
  const userProfile = useSelector((state) => state.manage.userProfile)

  const onDragEnter = useCallback(() => {
    setIsDragging(true)
  }, [])

  const onDragLeave = useCallback(() => {
    setIsDragging(false)
  }, [])

  const onSiteAction = (modalAction, data) => {
    let modalData
    if (data) {
      modalData = {
        ...data,
        domain: data.subdomain
      }
    }

    dispatch(showCreateSiteModal(true, modalData, modalAction))
  }

  const onCreateSite = (trialAccount, siteLimit) => {
    if (liveSitesCount >= siteLimit) {
      dispatch(
        showUpgradeCardModal({
          title: 'Upgrade to upload more content',
          desc: `Your current plan only allows ${siteLimit} live ${pluralize('project', 'projects', siteLimit)}. Upload more on a different plan.`,
          cta: 'Host more sites',
          onClick: siteLimit ? () => onUpgradePlan({ projects: siteLimit, userProfile }, dispatch) : null
        })
      )
    } else {
      onSiteAction(ACTION_CREATE)
    }
  }

  const onDropRejected = useCallback(() => {
    setIsDragging(false)
  }, [])

  const onFileDrop = useCallback(
    async (passedFiles) => {
      let acceptedFiles = passedFiles

      if (isFolderDrop(passedFiles)) {
        try {
          const zipFile = createMockFolderFile(passedFiles)
          acceptedFiles = [zipFile]
        } catch (error) {
          console.error('Error zipping folder', error)
          alert('Error processing the folder. Please try again.')
          setIsDragging(false)
          return
        }
      } else if (passedFiles.some((file) => file.path && file.path.startsWith('/'))) {
        alert('To upload a folder, please compress it into a ZIP file first')
        setIsDragging(false)
        return
      }

      const isTrial = !userProfile.productId
      const maxSizeMB = isTrial
        ? MAX_FILE_SIZE_FREE
        : userProfile.fileUploadLimit || PRO_MAX_FILE_SIZES[userProfile.productId]
      const isPro = userProfile.productId === PLANS.PRO_U.id

      if (acceptedFiles.length) {
        if (
          acceptedFiles.some((file) => isZipFile(file.type) && !isLottieFile(file.name)) &&
          acceptedFiles.length > 1
        ) {
          dispatch(setDraggedFile(null))
          setIsDragging(false)
          return
        }

        const validFiles = acceptedFiles.filter((file) => isValidFileSize(file, maxSizeMB))

        if (validFiles.length === acceptedFiles.length) {
          dispatch(setDraggedFile(acceptedFiles.length === 1 ? acceptedFiles[0] : acceptedFiles))
          setIsDragging(false)

          if (location.pathname === '/manage' && !updateModalOpen) {
            onCreateSite(trialAccount, siteLimit)
          }
        } else {
          const invalidFiles = acceptedFiles.filter((file) => !isValidFileSize(file, maxSizeMB))
          const largestInvalid = invalidFiles[0]

          if (!updateModalOpen) {
            dispatch(
              showUpgradeCardModal({
                title: 'Upgrade to upload larger files',
                desc: `Your current plan only allows you to upload ${isPDF(largestInvalid) ? 'PDFs' : 'files'} less than ${
                  isPDF(largestInvalid) && isTrial ? MAX_PDF_SIZE : maxSizeMB || MAX_FILE_SIZE
                }MB. ${invalidFiles.length > 1 ? `${invalidFiles.length} files are too large.` : 'Upload more on a different plan.'}`,
                cta: 'Upload larger files',
                onClick: largestInvalid?.size
                  ? () => onUpgradePlan({ fileSize: largestInvalid?.size, userProfile }, dispatch)
                  : null
              })
            )
          }
          setIsDragging(false)
        }
      } else {
        alert(
          'We currently support html, pdf, php, and zip files.\n\nLogin or Sign up to also be able to upload most popular image, raw image, document, powerpoint, excel formats!'
        )
        setIsDragging(false)
      }
    },
    [dispatch, location.pathname, onSiteAction, userProfile, updateModalOpen]
  )

  const { getRootProps, getInputProps, isDragAccept, isDragReject } = useDropzone({
    noClick: true,
    onDragEnter,
    onDragLeave,
    onDrop: onFileDrop,
    onDropRejected,
    accept: acceptedFiles,
    maxFilesize: 0.5
  })

  return (
    <div className={`dropzone-wrapper ${className || ''}`}>
      {isDragging && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            borderStyle: 'dashed',
            borderWidth: 1,
            border: isDragReject ? '2F5233' : isDragAccept ? ' #ff5c5c' : 'rgba(0, 0, 0, 0.5)',
            backgroundColor: isDragReject
              ? 'rgba(195, 20, 50, 0.85)'
              : isDragAccept
                ? 'rgba(17, 153, 142, 0.85)'
                : 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            pointerEvents: 'none',
            transition: 'all 0.3s ease-in-out'
          }}
        >
          <Placeholder isDragReject={isDragReject} />
        </div>
      )}
      <div {...getRootProps()}>
        <input {...getInputProps()} />
        {children}
      </div>
    </div>
  )
}

const mapStateToProps = ({ manage }) => ({
  userProfile: manage.userProfile
})

export default connect(mapStateToProps, undefined)(DropzoneWrapper)
